import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/adminlayout';
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/api';

const BanquetReports = () => {
  const [loading, setLoading] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [stats, setStats] = useState({
    totalStats: {},
    monthStats: {},
    upcomingCount: 0,
    revenueByType: [],
    hallStats: [],
    monthlyTrend: []
  });
  const [payments, setPayments] = useState([]);

  const colors = {
    primary: '#2D5016',
    accent: '#6ba83e',
    lightGray: '#f8faf9',
    mediumGray: '#e8ede9',
    textDark: '#1a1a1a',
    textLight: '#6b7280',
    white: '#ffffff',
    success: '#4a7c2a',
    warning: '#f59e0b',
    danger: '#ef4444'
  };

  const isMobile = windowWidth <= 768;
  const isTablet = windowWidth > 768 && windowWidth <= 1024;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    await Promise.all([fetchStats(), fetchPayments()]);
    setLoading(false);
  };

  const fetchStats = async () => {
    try {
      const data = await apiGet('/admin/banquet/reports/stats');
      if (data.success) setStats(data.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchPayments = async () => {
    try {
      const data = await apiGet('/admin/banquet/reports/payments');
      if (data.success) setPayments(data.data);
    } catch (error) {
      console.error('Error fetching payments:', error);
    }
  };

  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getPaymentStatusColor = (status) => {
    const statusColors = {
      paid: colors.success,
      partial: colors.warning,
      pending: colors.danger
    };
    return statusColors[status] || colors.textLight;
  };

  const styles = {
    pageHeader: {
      marginBottom: isMobile ? '16px' : '24px',
      padding: isMobile ? '0 4px' : '0'
    },
    pageTitle: {
      fontSize: isMobile ? '20px' : isTablet ? '24px' : '28px',
      fontWeight: '700',
      color: colors.textDark,
      marginBottom: '6px'
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
      gap: isMobile ? '12px' : '20px',
      marginBottom: isMobile ? '20px' : '24px',
      padding: isMobile ? '0 4px' : '0'
    },
    statCard: {
      background: colors.white,
      padding: isMobile ? '20px' : '24px',
      borderRadius: isMobile ? '14px' : '16px',
      boxShadow: '0 4px 12px rgba(45, 80, 22, 0.08)',
      border: `1px solid ${colors.mediumGray}`
    },
    statValue: {
      fontSize: isMobile ? '24px' : '32px',
      fontWeight: '800',
      marginBottom: '8px'
    },
    statLabel: {
      fontSize: isMobile ? '11px' : '12px',
      color: colors.textLight,
      textTransform: 'uppercase',
      fontWeight: '600'
    },
    contentCard: {
      background: colors.white,
      padding: isMobile ? '16px' : '24px',
      borderRadius: isMobile ? '14px' : '16px',
      boxShadow: '0 8px 24px rgba(45, 80, 22, 0.08)',
      border: `1px solid ${colors.mediumGray}`,
      margin: isMobile ? '0 4px 16px 4px' : '0 0 24px 0'
    },
    sectionTitle: {
      fontSize: isMobile ? '16px' : '20px',
      fontWeight: '700',
      color: colors.textDark,
      marginBottom: isMobile ? '12px' : '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: isMobile ? '12px' : '13px'
    },
    tableHeader: {
      background: colors.lightGray,
      fontWeight: '700',
      textAlign: 'left',
      padding: isMobile ? '10px' : '12px',
      borderBottom: `2px solid ${colors.mediumGray}`
    },
    tableCell: {
      padding: isMobile ? '10px' : '12px',
      borderBottom: `1px solid ${colors.lightGray}`
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
            <div>Loading reports...</div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div style={styles.pageHeader}>
        <h1 style={styles.pageTitle}>Banquet Reports</h1>
        <p style={{ fontSize: isMobile ? '12px' : '14px', color: colors.textLight }}>
          {isMobile ? 'Analytics and reports' : 'Banquet analytics, revenue reports, and performance metrics'}
        </p>
      </div>

      {/* Statistics Cards */}
      <div style={styles.statsGrid}>
        <div style={{ ...styles.statCard, borderTop: `4px solid ${colors.primary}` }}>
          <div style={{ ...styles.statValue, color: colors.primary }}>
            {stats.totalStats.total_bookings || 0}
          </div>
          <div style={styles.statLabel}>Total Bookings</div>
        </div>

        <div style={{ ...styles.statCard, borderTop: `4px solid ${colors.success}` }}>
          <div style={{ ...styles.statValue, color: colors.success }}>
            {formatCurrency(stats.totalStats.total_revenue)}
          </div>
          <div style={styles.statLabel}>Total Revenue</div>
        </div>

        <div style={{ ...styles.statCard, borderTop: `4px solid ${colors.accent}` }}>
          <div style={{ ...styles.statValue, color: colors.accent }}>
            {stats.monthStats.bookings_this_month || 0}
          </div>
          <div style={styles.statLabel}>This Month</div>
        </div>

        <div style={{ ...styles.statCard, borderTop: `4px solid ${colors.warning}` }}>
          <div style={{ ...styles.statValue, color: colors.warning }}>
            {stats.upcomingCount || 0}
          </div>
          <div style={styles.statLabel}>Upcoming Events</div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: isMobile ? '16px' : '20px' }}>
        {/* Revenue by Event Type */}
        <div style={styles.contentCard}>
          <h2 style={styles.sectionTitle}><span>📊</span> Revenue by Event Type</h2>
          {stats.revenueByType.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '30px', color: colors.textLight }}>
              No data available
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {stats.revenueByType.map((type, index) => {
                const maxRevenue = Math.max(...stats.revenueByType.map(t => parseFloat(t.revenue)));
                const percentage = (parseFloat(type.revenue) / maxRevenue) * 100;

                return (
                  <div key={index}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '13px' }}>
                      <span style={{ fontWeight: '600', textTransform: 'capitalize' }}>
                        {type.event_type.replace('_', ' ')}
                      </span>
                      <span style={{ color: colors.textLight }}>
                        {type.booking_count} booking{type.booking_count !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div style={{
                      height: '36px',
                      background: colors.primary,
                      borderRadius: '6px',
                      width: `${percentage}%`,
                      minWidth: '100px',
                      display: 'flex',
                      alignItems: 'center',
                      paddingLeft: '12px',
                      color: colors.white,
                      fontSize: '13px',
                      fontWeight: '700'
                    }}>
                      {formatCurrency(type.revenue)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Hall Performance */}
        <div style={styles.contentCard}>
          <h2 style={styles.sectionTitle}><span>🏛️</span> Hall Performance</h2>
          {stats.hallStats.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '30px', color: colors.textLight }}>
              No data available
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {stats.hallStats.map((hall, index) => (
                <div
                  key={index}
                  style={{
                    padding: isMobile ? '12px' : '14px',
                    background: colors.lightGray,
                    borderRadius: '8px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <div>
                    <div style={{ fontSize: isMobile ? '14px' : '15px', fontWeight: '600', color: colors.textDark }}>
                      {hall.hall_name}
                    </div>
                    <div style={{ fontSize: '11px', color: colors.textLight }}>
                      {hall.booking_count || 0} booking{hall.booking_count !== 1 ? 's' : ''}
                    </div>
                  </div>
                  <div style={{ fontSize: isMobile ? '15px' : '16px', fontWeight: '700', color: colors.primary }}>
                    {formatCurrency(hall.revenue)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Monthly Trend */}
      <div style={styles.contentCard}>
        <h2 style={styles.sectionTitle}><span>📈</span> Monthly Trend (Last 6 Months)</h2>
        {stats.monthlyTrend.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '30px', color: colors.textLight }}>
            No trend data available
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.tableHeader}>Month</th>
                  <th style={styles.tableHeader}>Bookings</th>
                  <th style={styles.tableHeader}>Revenue</th>
                </tr>
              </thead>
              <tbody>
                {stats.monthlyTrend.map((month, index) => (
                  <tr key={index}>
                    <td style={styles.tableCell}>
                      <strong>{month.month}</strong>
                    </td>
                    <td style={styles.tableCell}>{month.bookings}</td>
                    <td style={styles.tableCell}>
                      <strong>{formatCurrency(month.revenue)}</strong>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Payment Status Report */}
      <div style={styles.contentCard}>
        <h2 style={styles.sectionTitle}><span>💰</span> Payment Status Report</h2>
        {payments.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '30px', color: colors.textLight }}>
            No payment data available
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.tableHeader}>Booking #</th>
                  <th style={styles.tableHeader}>Customer</th>
                  {!isMobile && <th style={styles.tableHeader}>Event Date</th>}
                  <th style={styles.tableHeader}>Total</th>
                  <th style={styles.tableHeader}>Advance</th>
                  <th style={styles.tableHeader}>Balance</th>
                  <th style={styles.tableHeader}>Status</th>
                </tr>
              </thead>
              <tbody>
                {payments.map(payment => (
                  <tr key={payment.id}>
                    <td style={styles.tableCell}><strong>{payment.booking_number}</strong></td>
                    <td style={styles.tableCell}>{payment.customer_name}</td>
                    {!isMobile && (
                      <td style={styles.tableCell}>{formatDate(payment.event_date)}</td>
                    )}
                    <td style={styles.tableCell}>
                      <strong>{formatCurrency(payment.total_amount)}</strong>
                    </td>
                    <td style={styles.tableCell}>{formatCurrency(payment.advance_paid)}</td>
                    <td style={styles.tableCell}>
                      <strong style={{ color: parseFloat(payment.balance_amount) > 0 ? colors.danger : colors.success }}>
                        {formatCurrency(payment.balance_amount)}
                      </strong>
                    </td>
                    <td style={styles.tableCell}>
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '6px',
                        fontSize: '11px',
                        fontWeight: '600',
                        background: `${getPaymentStatusColor(payment.payment_status)}20`,
                        color: getPaymentStatusColor(payment.payment_status)
                      }}>
                        {payment.payment_status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default BanquetReports;